import {
  type ContextoDeRespuesta,
  catalogosDelFormulario,
  type FilaDeGrupo,
  formularioPorId,
  type Pregunta,
  progresoDel,
  type Respuesta,
  type Respuestas,
  respuestasVigentes,
  siguientePregunta,
} from '@pasos/schemas';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { EcoDeRespuesta } from '../components/domain/EcoDeRespuesta.js';
import { EncabezadoDeFlujo } from '../components/domain/EncabezadoDeFlujo.js';
import { PantallaDeCierre } from '../components/domain/PantallaDeCierre.js';
import { Segmentos } from '../components/domain/Segmentos.js';
import { BannerOffline } from '../components/ui/BannerOffline.js';
import { Boton } from '../components/ui/Boton.js';
import { ErrorDeCarga } from '../components/ui/Estado.js';
import { PACIENTE } from '../copy/index.js';
import { useEstadoDeConexion } from '../lib/conexion.js';
import {
  type Borrador,
  borrarBorrador,
  codigoDelError,
  type EnvioPendiente,
  encolar,
  enviosPorSalir,
  guardarBorrador,
  leerBorrador,
  marcarRechazado,
} from './almacen.js';
import * as api from './api.js';
import { type CatalogosDescargados, useCatalogoDeMedicamentos } from './catalogos.js';
import { enviarCompleto, intentarElEnvio, queHacerCon } from './cola.js';
import { textoDeRespuesta } from './eco.js';
import { Arbol } from './preguntas/Arbol.js';
import { Caras, caraDe } from './preguntas/Caras.js';
import { ControlEscalar, Grupo } from './preguntas/Grupo.js';
import { Opciones } from './preguntas/Opciones.js';
import { Termometro } from './preguntas/Termometro.js';

type FormularioProps = {
  formularioId: string;
  hito: string | null;
  onTerminar?: (() => void) | undefined;
  /**
   * `gracias` (por defecto) muestra el cierre; `seguir` no muestra nada y deja
   * que quien lo usa pase al formulario siguiente: una evaluación de hito son
   * varias escalas seguidas, y el "Gracias" va una sola vez, al final.
   */
  alCerrar?: 'gracias' | 'seguir';
  /** A dónde lleva el botón de volver del encabezado. */
  volverA?: string;
  /**
   * Hoy y el día del egreso, para el rango de una fecha (E3.25). Lo arma quien
   * tiene el inicio del paciente; sin él, la fecha se valida en el servidor.
   */
  contexto?: ContextoDeRespuesta;
  /**
   * Lo que se muestra antes de una pregunta, cuando la pregunta lo necesita
   * (E3.25): en D+360, lo que la persona ya contó en el año, antes de
   * preguntarle si falta alguna.
   */
  antesDeLaPregunta?: ((pregunta: Pregunta) => ReactNode) | undefined;
};

/**
 * Microcheck (P-05) y escala (P-06) · una pregunta por pantalla.
 *
 * **El recorrido lo calcula el cliente con la misma función que el servidor.**
 * `@pasos/schemas` viaja compilado adentro de la PWA, así que la pantalla sabe
 * cuál es la pregunta siguiente sin preguntarle nada a nadie — que es lo que
 * hace posible responder sin conexión (S-08) y lo que evita una pantalla de
 * espera entre pregunta y pregunta. El servidor vuelve a calcular todo al
 * cerrar: acá no hay una segunda fuente de verdad, hay la misma función corriendo
 * en los dos lados.
 *
 * **Todo se guarda local primero.** Cada respuesta va al borrador antes de
 * intentar el envío. Si la red no está —o se cae a mitad—, la aplicación
 * terminada queda en la cola y se manda al reconectar. El paciente ve el banner
 * y sigue; nunca ve un error por algo que no depende de él.
 *
 * E3.8.2, contra la referencia P-05 a P-06: encabezado con volver, título y
 * "Pregunta 2 de 4"; el avance en segmentos; la pregunta en serif de 30 px; y el
 * cierre con las dos cápsulas del logo. **La redacción de las preguntas no se
 * toca**: viene del instrumento, en `@pasos/schemas`.
 *
 * E3.9 · P-74, aprobado por Mariano: **el formulario no avanza solo al tocar una
 * opción.** El paciente elige, ve el eco "Tu respuesta" y confirma con
 * "Siguiente"; en la última pregunta el botón es "Enviar mis respuestas". Para un
 * adulto mayor con el teléfono en una mano, un toque errado ya no es una
 * respuesta. Lo elegido y todavía no confirmado vive sólo en la pantalla: el
 * borrador, el envío incremental y la cola offline siguen recibiendo nada más
 * que respuestas confirmadas, igual que antes.
 */
export function Formulario({
  formularioId,
  hito,
  onTerminar,
  alCerrar = 'gracias',
  volverA = '/inicio',
  contexto = SIN_CONTEXTO,
  antesDeLaPregunta,
}: FormularioProps) {
  const formulario = useMemo(() => formularioPorId(formularioId), [formularioId]);
  const enLinea = useEstadoDeConexion();
  const usaMedicamentos = catalogosDelFormulario(formulario).includes('medicamentos');
  const { data: medicamentos } = useCatalogoDeMedicamentos(usaMedicamentos);
  const catalogos: CatalogosDescargados = useMemo(
    () => (medicamentos ? { medicamentos: medicamentos.entradas } : {}),
    [medicamentos],
  );

  const [borrador, setBorrador] = useState<Borrador>(
    () =>
      leerBorrador(formularioId, hito) ?? {
        aplicacionId: null,
        respuestas: {},
        iniciadoAt: Date.now(),
      },
  );
  const [enviando, setEnviando] = useState(false);
  const [terminado, setTerminado] = useState<'enviado' | 'en-cola' | 'rechazado' | null>(null);
  const [pendientes, setPendientes] = useState(() => enviosPorSalir().length);
  // Lo que el paciente tocó en la pregunta actual y todavía no confirmó.
  const [elegida, setElegida] = useState<{ pregunta: string; valor: Respuesta } | null>(null);

  const respuestas: Respuestas = borrador.respuestas;
  const actual = siguientePregunta(formulario, respuestas);
  const progreso = progresoDel(formulario, respuestas);

  const persistir = useCallback(
    (siguiente: Borrador) => {
      setBorrador(siguiente);
      guardarBorrador(formularioId, hito, siguiente);
    },
    [formularioId, hito],
  );

  // Abre la aplicación en el servidor apenas hay conexión. Es idempotente: si
  // ya estaba abierta devuelve la misma, así que reintentarlo no duplica nada.
  useEffect(() => {
    if (!enLinea || borrador.aplicacionId) return;

    let vigente = true;
    api
      .abrirFormulario(formularioId, hito)
      .then((aplicacion) => {
        if (vigente) persistir({ ...borrador, aplicacionId: aplicacion.id });
      })
      .catch(() => {
        // Sin id del servidor se sigue igual: al cerrar, el envío completo
        // (abrir + respuestas + cierre) va a la cola.
      });

    return () => {
      vigente = false;
    };
  }, [enLinea, borrador, formularioId, hito, persistir]);

  // Al volver la conexión, se manda lo que haya quedado esperando. La regla es
  // la de toda la app (`intentarElEnvio`, P-132) y el vaciado no se pisa con el
  // suyo: acá sólo se mira el resultado para actualizar el banner.
  useEffect(() => {
    if (!enLinea) return;
    void intentarElEnvio()?.then(({ pendientes: quedan }) => setPendientes(quedan));
  }, [enLinea]);

  const valorElegido = actual && elegida?.pregunta === actual.id ? elegida.valor : undefined;
  const vigentesConLaElegida =
    actual && valorElegido !== undefined
      ? respuestasVigentes(formulario, { ...respuestas, [actual.id]: valorElegido })
      : null;
  // Es la última si, confirmada la elegida, el recorrido no tiene otra pregunta.
  // Se calcula con la misma función del recorrido: el árbol condicional decide.
  const esLaUltima =
    vigentesConLaElegida !== null
      ? siguientePregunta(formulario, vigentesConLaElegida) === null
      : progreso.numero === progreso.total;

  function responder(valor: Respuesta): Respuestas | null {
    if (!actual) return null;

    const fusionadas = { ...respuestas, [actual.id]: valor };
    const vigentes = respuestasVigentes(formulario, fusionadas);
    persistir({ ...borrador, respuestas: vigentes });
    setElegida(null);

    // Envío incremental, sin bloquear la pantalla: si falla, el borrador local
    // ya tiene la respuesta y el cierre la vuelve a mandar entera.
    if (borrador.aplicacionId) {
      void api.guardarRespuestas(borrador.aplicacionId, vigentes).catch(() => undefined);
    }
    return vigentes;
  }

  /** "Siguiente": confirma la elegida; en la última, además envía. */
  function confirmar() {
    if (valorElegido === undefined) return;
    const vigentes = responder(valorElegido);
    if (vigentes && esLaUltima) void cerrar('completa', vigentes);
  }

  async function cerrar(cierre: 'completa' | 'parcial', finales: Respuestas = respuestas) {
    setEnviando(true);

    const envio: EnvioPendiente = {
      formularioId,
      hito,
      respuestas: finales,
      cierre,
      duracionSegundos: Math.round((Date.now() - borrador.iniciadoAt) / 1000),
      // La hora del dispositivo, que viaja como dato (H-6): es lo único que
      // permite que quien respondió en término sin señal no figure como tardío
      // cuando la cola sale dos días después. No decide: el servidor la
      // contrasta con la ventana del contacto y con su propio reloj.
      respondidoAt: Date.now(),
    };

    try {
      await enviarCompleto(envio);
      borrarBorrador(formularioId, hito);
      setTerminado('enviado');
    } catch (error) {
      // **La misma decisión que el vaciado, no una copia** (`cola.ts`). Hasta
      // E3.23 este catch encolaba cualquier error y le decía al paciente que
      // había quedado guardado: también cuando el servidor lo había rechazado
      // para siempre. Es el daño de H-1 por la puerta de adelante — le
      // prometemos que va a salir algo que no va a salir nunca.
      const que = queHacerCon(error);

      if (que === 'entregado') {
        // Un 409 quiere decir que esta aplicación ya está cerrada del otro lado:
        // llegó. No hay nada que encolar ni nada que retomar.
        borrarBorrador(formularioId, hito);
        setTerminado('enviado');
      } else if (que === 'rechazado') {
        // Se guarda igual —perder la respuesta en silencio sería peor—, pero
        // marcada, así no espera un envío que no va a ocurrir y el contacto
        // vuelve a ofrecerse en el Inicio para que pueda responderlo de nuevo.
        encolar(envio);
        marcarRechazado(formularioId, hito, codigoDelError(error));
        setPendientes(enviosPorSalir().length);
        setTerminado('rechazado');
      } else {
        encolar(envio);
        // Lo rechazado no cuenta: el banner dice qué está por salir, no qué hay guardado.
        setPendientes(enviosPorSalir().length);
        setTerminado('en-cola');
      }
    } finally {
      setEnviando(false);
    }

    onTerminar?.();
  }

  if (terminado) {
    if (alCerrar === 'seguir') return null;
    return <Cerrado estado={terminado} titulo={formulario.titulo} />;
  }

  return (
    <section className="flex flex-col">
      <EncabezadoDeFlujo
        etiquetaDeVolver={PACIENTE.navegacion.volver}
        paso={PACIENTE.formulario.progreso(progreso.numero, progreso.total)}
        titulo={formulario.titulo}
        volverA={volverA}
      />
      <div className="bg-card px-5 pb-4">
        <Segmentos actual={progreso.numero} total={progreso.total} />
      </div>

      <div className="flex flex-col gap-6 px-5 pt-6">
        {enLinea ? null : <BannerOffline pendientes={pendientes} />}

        {actual ? (
          <PantallaDePregunta
            antes={antesDeLaPregunta?.(actual)}
            catalogos={catalogos}
            contexto={contexto}
            elegida={valorElegido}
            // La pieza de cada pregunta tiene su propio estado (lo escrito en un
            // número, las filas de un grupo): con la clave, la pregunta
            // siguiente arranca de cero aunque sea del mismo tipo.
            key={actual.id}
            onElegir={(valor) =>
              setElegida(valor === undefined ? null : { pregunta: actual.id, valor })
            }
            pregunta={actual}
            valor={valorElegido ?? respuestas[actual.id]}
          />
        ) : (
          <p className="m-0 text-lg leading-relax text-foreground">
            {PACIENTE.formulario.sinPreguntas}
          </p>
        )}

        <div className="flex flex-col gap-2 pb-6">
          {actual ? (
            <Boton cargando={enviando} disabled={valorElegido === undefined} onClick={confirmar}>
              {esLaUltima ? PACIENTE.formulario.enviar : PACIENTE.formulario.siguiente}
            </Boton>
          ) : (
            <Boton cargando={enviando} onClick={() => void cerrar('completa')}>
              {PACIENTE.formulario.enviar}
            </Boton>
          )}

          {/* Skip suave (P-05): irse no es un error y no se pregunta dos veces.
              Lo respondido queda guardado y la escala cuenta como intentada. */}
          <Boton disabled={enviando} onClick={() => void cerrar('parcial')} variante="texto">
            {PACIENTE.formulario.masTarde}
          </Boton>
        </div>
      </div>
    </section>
  );
}

/** Sin contexto: la fecha se valida sólo por su forma, y el rango lo mira el servidor. */
const SIN_CONTEXTO: ContextoDeRespuesta = {};

function PantallaDePregunta({
  pregunta,
  valor,
  elegida,
  onElegir,
  contexto,
  catalogos,
  antes,
}: {
  /** Lo que va antes del enunciado, si la pregunta lo necesita. */
  antes?: ReactNode;
  pregunta: Pregunta;
  valor: Respuesta | undefined;
  /** Lo tocado y no confirmado: es lo que repite el eco. */
  elegida: Respuesta | undefined;
  /** `undefined` es "todavía no hay una respuesta válida": lo usan las piezas de E3.25. */
  onElegir: (valor: Respuesta | undefined) => void;
  contexto: ContextoDeRespuesta;
  catalogos: CatalogosDescargados;
}) {
  // En el árbol el enunciado lo pone el propio nodo, que va cambiando: la raíz
  // es la pregunta del dominio y las de adentro son otras. Repetir acá el texto
  // de la pregunta dejaría dos preguntas en la misma pantalla —y en la primera
  // pantalla, la misma dos veces—, que es exactamente lo que "una pregunta por
  // pantalla" quiere evitar.
  const conEnunciadoPropio = pregunta.presentacion.modo === 'arbol';
  const idEnunciado = `pregunta-${pregunta.id}`;

  return (
    <div className="flex flex-col gap-5">
      {antes ?? null}
      {conEnunciadoPropio ? null : (
        <h2
          className="m-0 font-serif text-3xl leading-tight font-regular tracking-tight text-heading"
          id={idEnunciado}
        >
          {pregunta.texto}
        </h2>
      )}

      {pregunta.ayuda ? <p className="m-0 -mt-3 text-lg text-subtle">{pregunta.ayuda}</p> : null}

      <Control
        catalogos={catalogos}
        contexto={contexto}
        etiquetadoPor={idEnunciado}
        onResponder={onElegir}
        pregunta={pregunta}
        valor={valor}
      />

      <EcoDeRespuesta
        etiqueta={PACIENTE.formulario.tuRespuesta}
        icono={
          elegida !== undefined &&
          pregunta.presentacion.modo === 'emoji' &&
          pregunta.campo.tipo === 'puntaje' &&
          typeof elegida === 'number'
            ? caraDe(elegida, pregunta.campo.minimo, pregunta.campo.maximo)
            : undefined
        }
        valor={elegida === undefined ? null : textoDeRespuesta(pregunta, elegida, catalogos)}
      />
    </div>
  );
}

function Control({
  pregunta,
  valor,
  onResponder,
  etiquetadoPor,
  contexto,
  catalogos,
}: {
  pregunta: Pregunta;
  valor: Respuesta | undefined;
  onResponder: (valor: Respuesta | undefined) => void;
  etiquetadoPor: string;
  contexto: ContextoDeRespuesta;
  catalogos: CatalogosDescargados;
}) {
  const { campo, presentacion } = pregunta;

  // Los cuatro tipos de E3.25 se presentan con su control propio.
  if (presentacion.modo === 'propia') {
    return (
      <ControlPropio
        catalogos={catalogos}
        contexto={contexto}
        etiquetadoPor={etiquetadoPor}
        onResponder={onResponder}
        pregunta={pregunta}
        valor={valor}
      />
    );
  }

  if (presentacion.modo === 'arbol') {
    return <Arbol arbol={presentacion.arbol} onResolver={onResponder} preguntaId={pregunta.id} />;
  }

  if (presentacion.modo === 'emoji' && campo.tipo === 'puntaje') {
    return (
      <Caras
        campo={campo}
        etiquetadoPor={etiquetadoPor}
        onElegir={onResponder}
        valor={typeof valor === 'number' ? valor : undefined}
      />
    );
  }

  if (presentacion.modo === 'termometro' && campo.tipo === 'puntaje') {
    return (
      <Termometro
        campo={campo}
        etiquetadoPor={etiquetadoPor}
        onElegir={onResponder}
        valor={typeof valor === 'number' ? valor : undefined}
      />
    );
  }

  // Todo lo demás es una lista: opciones, binario, y los puntajes de rango corto
  // declarados como `lista` —las cinco dimensiones del EQ-5D-5L—. Las caras son
  // del microcheck y sólo del microcheck (ADR-016, enmienda del 25/08): una
  // escala validada no se responde con caritas. Los tipos de E3.25 no llegan
  // acá: la coherencia de la definición los obliga al control propio.
  if (campo.tipo !== 'opcion' && campo.tipo !== 'binario' && campo.tipo !== 'puntaje') return null;
  return (
    <Opciones campo={campo} etiquetadoPor={etiquetadoPor} onElegir={onResponder} valor={valor} />
  );
}

/** La pieza de un tipo de E3.25: el grupo con sus filas, o la de su tipo escalar. */
function ControlPropio({
  pregunta,
  valor,
  onResponder,
  etiquetadoPor,
  contexto,
  catalogos,
}: {
  pregunta: Pregunta;
  valor: Respuesta | undefined;
  onResponder: (valor: Respuesta | undefined) => void;
  etiquetadoPor: string;
  contexto: ContextoDeRespuesta;
  catalogos: CatalogosDescargados;
}) {
  const { campo } = pregunta;
  if (campo.tipo === 'grupo') {
    return (
      <Grupo
        campo={campo}
        catalogos={catalogos}
        contexto={contexto}
        onElegir={onResponder}
        valor={Array.isArray(valor) ? (valor as FilaDeGrupo[]) : undefined}
      />
    );
  }
  return (
    <ControlEscalar
      campo={campo}
      catalogos={catalogos}
      contexto={contexto}
      etiquetadoPor={etiquetadoPor}
      onElegir={onResponder}
      valor={Array.isArray(valor) ? undefined : valor}
    />
  );
}

/**
 * El cierre del formulario · tres salidas, y cada una dice la verdad.
 *
 * El rechazo definitivo **no usa la pantalla de gracias**: agradecerle a alguien
 * por algo que no se registró, y encima decirle que se envía solo, es la mentira
 * que E3.23 vino a sacar. Va con el estado de error del sistema (S-01), que es
 * el que ya existe para esto, y con su contacto todavía disponible.
 */
function Cerrado({
  estado,
  titulo,
}: {
  estado: 'enviado' | 'en-cola' | 'rechazado';
  titulo: string;
}) {
  if (estado === 'rechazado') {
    return (
      <div className="flex min-h-dvh flex-col justify-center gap-4 px-5 py-8">
        <ErrorDeCarga
          detalle={PACIENTE.formulario.noSePudoRegistrarDetalle}
          titulo={PACIENTE.formulario.noSePudoRegistrar}
        />
        <Link
          className="flex min-h-15 items-center justify-center rounded-lg bg-brand px-6 text-touch font-bold text-primary-foreground no-underline shadow-glow-navy"
          to="/inicio"
        >
          {PACIENTE.formulario.volverAlInicio}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <PantallaDeCierre
        texto={
          estado === 'en-cola'
            ? PACIENTE.formulario.enCola(titulo)
            : PACIENTE.formulario.enviado(titulo)
        }
        titulo={PACIENTE.formulario.graciasTitulo}
        variante="checkin"
      />
      <div className="px-5 pt-4 pb-6">
        <Link
          className="flex min-h-15 items-center justify-center rounded-lg bg-brand px-6 text-touch font-bold text-primary-foreground no-underline shadow-glow-navy"
          to="/inicio"
        >
          {PACIENTE.formulario.volverAlInicio}
        </Link>
      </div>
    </div>
  );
}
